import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/lenis";
import { profile } from "@/data/portfolio";

const links = [
  { label: "About", id: "#about" },
  { label: "Skills", id: "#skills" },
  { label: "Projects", id: "#projects" },
  { label: "Experience", id: "#experience" },
  { label: "Life", id: "#beyond" },
  { label: "AI Lab", id: "#playground" },
  { label: "Contact", id: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-white/5 py-3" : "py-5"
      }`}
      data-testid="main-navbar"
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
        <button
          data-testid="nav-logo-button"
          onClick={() => go("#home")}
          className="font-mono text-sm font-bold tracking-widest text-slate-100"
        >
          AZ<span className="text-cyan-400">.</span>DS
        </button>

        <div className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => go(l.id)}
              className="font-mono text-xs uppercase tracking-widest text-slate-400 transition-colors duration-300 hover:text-cyan-400"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden items-center gap-4 md:flex">
          <span
            data-testid="nav-status-indicator"
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-400"
          >
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-emerald-400" />
            {profile.status}
          </span>
          <a
            data-testid="nav-download-cv-button"
            href={profile.cvUrl}
            download
            className="flex items-center gap-2 rounded-full border border-violet-500/40 bg-violet-500/10 px-4 py-2 font-mono text-xs uppercase tracking-widest text-violet-300 transition-all duration-300 hover:bg-violet-500/25 hover:glow-violet"
          >
            <Download size={14} /> CV
          </a>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="text-slate-200 md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div className="glass mt-2 border-t border-white/5 px-6 py-4 md:hidden" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => go(l.id)}
              className="block w-full py-3 text-left font-mono text-sm uppercase tracking-widest text-slate-300"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-mobile-download-cv-button"
            href={profile.cvUrl}
            download
            className="mt-2 flex w-fit items-center gap-2 rounded-full border border-violet-500/40 px-4 py-2 font-mono text-xs uppercase tracking-widest text-violet-300"
          >
            <Download size={14} /> Download CV
          </a>
        </div>
      )}
    </motion.header>
  );
};
